<div class="aems-bread-crumb m-b-16">
  <a class="m-r-4" routerLink="/master-data">Master Data</a>
  <span class="m-r-4">/ </span>
  <a class="m-r-4" routerLink="/master-data/repair-code-exclusion">Repair Code Exclusion</a>
  <span>/ {{ isEditMode() ? 'Edit' : 'Add New' }}</span>
</div>

<div class="page-header m-b-12">
  <h3>{{ isEditMode() ? 'Edit' : 'Add New' }}</h3>
  @if (isEditMode()) {
    <div class="page-header-actions">
      <mc-button
        fit="small"
        variant="outlined"
        icon="arrow-anti-clockwise-clock"
        (click)="onAuditLogClick()"
        (keydown.enter)="onAuditLogClick()"
        (keydown.space)="$event.preventDefault(); onAuditLogClick()">
        Audit log
      </mc-button>
    </div>
  }
</div>

@if (isEditMode() && detailData()) {
  <div class="mds-text--body-normal mds-neutral--weak__text-color m-b-12">
    Last updated on {{ detailData()?.updatedAt | date: "d MMM yyyy 'at' HH:mm:ss" }} by
    {{ detailData()?.updatedBy || 'User' }}
  </div>
}

<form [formGroup]="form">
  @if (formSubmitErrors().length > 0) {
    <div class="error m-b-8">
      <mc-icon class="error-icon" icon="exclamation-octagon" size="20"></mc-icon>
      <div class="title">Errors</div>
      <ul>
        @for (error of formSubmitErrors(); track $index) {
          <li>{{ error }}</li>
        }
      </ul>
    </div>
  }
  <div class="flex-column">
    @if (formSubmitErrors().length === 0) {
      <app-error [formContainer]="form"></app-error>
    }
  </div>

  <div class="pure-g">
    <div class="pure-u-1 pure-u-md-3-3 pure-u-lg-3-3 edit-card-wrapper">
      <div class="aems-card m-b-16">
        <app-repair-code-exclusion-information [formGroup]="form" [isEditMode]="isEditMode()">
        </app-repair-code-exclusion-information>

        <div class="exclusive-code-row m-b-12">
          <mc-input
            ngDefaultControl
            class="exclusive-code-input"
            label="Exclusive repair code"
            placeholder="Enter repair code"
            fit="small"
            formControlName="exclusiveRepairCodeInput"
            (keyup.enter)="addExclusiveRepairCode()">
          </mc-input>
          <mc-button fit="small" variant="outlined" appearance="neutral" (click)="addExclusiveRepairCode()" (keydown.enter)="addExclusiveRepairCode()"
            >Add</mc-button
          >
        </div>

        <div class="exclusive-codes-table">
          <div class="exclusive-codes-header">Repair Code</div>
          @if (exclusiveRepairCodes().length === 0) {
            <div class="exclusive-codes-empty">No exclusive repair codes added.</div>
          } @else {
            @for (code of exclusiveRepairCodes(); track code) {
              <div class="exclusive-code-item">
                <span>{{ code }}</span>
                <mc-button
                  fit="small"
                  variant="plain"
                  appearance="neutral"
                  hiddenlabel
                  icon="trash"
                  padding="compact"
                  (click)="removeExclusiveRepairCode(code)"
                  (keydown.enter)="removeExclusiveRepairCode(code)"></mc-button>
              </div>
            }
          }
        </div>
      </div>
    </div>

    <div class="page-footer-actions m-t-16">
      <div>
        @if (isEditMode()) {
          <mc-button
            type="button"
            variant="outlined"
            appearance="neutral"
            fit="small"
            icon="trash"
            (click)="openDeleteRuleDialog.set(true)"
            (keydown.enter)="openDeleteRuleDialog.set(true)">
            Delete Rule
          </mc-button>
        }
      </div>
      <div class="right-actions">
        <mc-button
          type="button"
          variant="outlined"
          appearance="neutral"
          fit="small"
          (click)="onCancel()"
          (keydown.enter)="onCancel()"
          (keydown.space)="$event.preventDefault(); onCancel()">
          Cancel
        </mc-button>
        <mc-button
          type="button"
          variant="filled"
          appearance="primary"
          fit="small"
          [disabled]="isEditMode() ? !form.dirty || form.invalid || submitLoader() : form.invalid || submitLoader()"
          [loading]="submitLoader()"
          (click)="onSubmit()"
          (keydown.enter)="onSubmit()"
          (keydown.space)="$event.preventDefault(); onSubmit()">
          {{ isEditMode() ? 'Save changes' : 'Add exclusion' }}
        </mc-button>
      </div>
    </div>
  </div>
</form>

<!-- Cancel Confirmation Modal -->
@if (showCancelDialog()) {
  <mc-modal heading="Unsaved Changes" fit="small" dimension="small" [open]="showCancelDialog()" (closed)="keepEditing()">
    <p>Are you sure you want to cancel? All unsaved changes will be lost permanently.</p>
    <mc-button
      slot="secondaryAction"
      dialogaction="cancel"
      appearance="neutral"
      variant="filled"
      (click)="keepEditing()"
      (keydown.enter)="keepEditing()"
      (keydown.space)="$event.preventDefault(); keepEditing()">
      Keep Changes
    </mc-button>
    <mc-button
      slot="primaryAction"
      appearance="primary"
      variant="filled"
      (click)="confirmCancel()"
      (keydown.enter)="confirmCancel()"
      (keydown.space)="$event.preventDefault(); confirmCancel()">
      Discard changes
    </mc-button>
  </mc-modal>
}

<!-- Delete Rule Confirmation Modal -->
@if (openDeleteRuleDialog()) {
  <mc-modal heading="Delete Rule" fit="small" dimension="small" [open]="openDeleteRuleDialog()" (closed)="openDeleteRuleDialog.set(false)">
    <p>Are you sure you want to delete this rule?</p>
    <mc-button
      slot="secondaryAction"
      dialogaction="cancel"
      appearance="neutral"
      variant="filled"
      (click)="openDeleteRuleDialog.set(false)"
      (keydown.enter)="openDeleteRuleDialog.set(false)">
      Cancel
    </mc-button>
    <mc-button
      slot="primaryAction"
      appearance="primary"
      variant="filled"
      (click)="onDeleteRule()"
      (keydown.enter)="onDeleteRule()">
      Delete Rule
    </mc-button>
  </mc-modal>
}

<!-- Audit Log Modal -->
<mc-modal
  backdropcloseactiondisabled
  heading="Audit Log"
  fit="small"
  dimension="large"
  [open]="openAuditLog"
  (closed)="openAuditLog = false">
  @if (openAuditLog) {
    <app-master-data-audit-log [transactionCode]="repairCode" label="Repair Code Exclusion" [auditLogFetcher]="auditLogFetcher">
    </app-master-data-audit-log>
  }
</mc-modal>
